/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import {ClassNameMap} from '@material-ui/core/styles/withStyles';
import isEqual from 'lodash.isequal';
import {get} from '@ion/core/src/environment/environment';
import {translate} from '@ion/core/src/locale/locale';
import {
  getAccountServiceProfile,
  getSubscription
} from '@ion/core/src/services/Connect';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {
  Account,
  AccountLink,
  Entity,
  EntityStatus,
  GenericObject,
  ServiceProfile
} from '@ion/core/src/types/entities';
import CheckBox from '@ion/dls/src/components/checkbox/CheckBox';
import Chip from '@ion/dls/src/components/chip/Chip';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import {FormSection} from '@ion/dls/src/components/forms/FormSection';
import {FormTextField} from '@ion/dls/src/components/forms/FormTextField';
import Switch from '@ion/dls/src/components/switch/Switch';
import EntityStatusChooser from '@ion/dls/src/editors/EntityStatusChooser';
import GroupChooser from '@ion/dls/src/editors/GroupChooser';
import {AccountFeature, GroupFormFields, MultiAuthStatus} from '@ion/dls/src/editors/GroupEditorUtils';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import ConditionalRender from '@ion/dls/src/render/ConditionalRender';
import Render from '@ion/dls/src/render/Render';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {BodyTypography} from '@ion/dls/src/typography/BodyTypography';
import {ServiceProfileChooser} from '@ion/features/src/entities/serviceProfile/ServiceProfileChooser';
import {SubscriptionChooser} from '@ion/features/src/entities/subscription/SubscriptionChooser';
import {useVbusAccess} from '../../devices/useVbusAccess';
import AccountTooltip from './AccountTooltip';
import AccountType from './AccountType';
import AutojobsSettingConfirmationDialog from './AutojobsSettingConfirmationDialog';
import AutojobsTooltip from './AutojobsTooltip';
import MultiAuthConfirmationDialog from './MultiAuthConfirmationDialog';

export interface AccountInfoProps {
  handleSolomonFieldChange?: Function;
  handleExternalRefIdFieldChange?: Function
  handleFieldChange?: Function;
  handleChooserChange?: Function;
  handleSwitchChange?: (event: ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  classes?: ClassNameMap<string>,
  group?: Account | GenericObject;
  parentAccount?: Account;
  defaultAccount?: AccountLink;
  account: Account;
  formState?: object;
  isSameGroup?: boolean;
  isViewMode?: boolean;
  isEditMode?: boolean;
  open?: boolean;
  isLoggedinUserAccount?: boolean;
  showServiceProfile?: boolean;
  isCalampSupport?: boolean;
  hasAutoJobs?: boolean;
  autojobsSetting?: boolean;
  isCalAmpRootAccount: boolean;
  hasSSOConfig?: boolean;
};

const multiAuthPendingMessageKey = {
  [MultiAuthStatus.PendingDisable]        : 'admin-groups:dialog.multiAuth.pendingDisable',
  [MultiAuthStatus.PendingAccountEnabled] : 'admin-groups:dialog.multiAuth.pendingAccountEnabled',
  [MultiAuthStatus.PendingEnable]         : 'admin-groups:dialog.multiAuth.pendingEnable'
};

const buildFieldEvent = (name: string, value: boolean) => (
  {target: {name, value, checked: value}} as unknown as ChangeEvent<HTMLInputElement>
);

/**
 * The AccountInfo component provides the UI which allows to add/edit
 *  fields such as their Applications, UnitOfMeasure, and TimeZone on
 *  account creation/updation.
 *
 *    import {AccountInfo} from '@ion/applications/src/pages/admin/accounts/AccountInfo';
 *
 *    <AccountInfo
 *       onChange={onChange} />
 *
 */
export const AccountInfo = ({
  handleSolomonFieldChange,
  handleExternalRefIdFieldChange,
  handleChooserChange,
  handleSwitchChange,
  handleFieldChange,
  defaultAccount,
  parentAccount,
  formState,
  account,
  classes,
  isCalAmpRootAccount,
  group = {},
  isSameGroup = false,
  isViewMode = false,
  isEditMode = false,
  open = false,
  showServiceProfile = false,
  isLoggedinUserAccount = false,
  isCalampSupport = false,
  hasAutoJobs = false,
  autojobsSetting = false,
  hasSSOConfig = false
}: AccountInfoProps) => {
  const {isPhone}                                                 = useMedia();
  const [enterpriseApps, setEnterpriseApps]                       = useState<string[]>([]);
  const [defaultProfile, setDefaultProfile]                       = useState<ServiceProfile>();
  const [showConfirmation, setShowConfirmation]                   = useState(false);
  const [showMultiAuthConfirmation, setShowMultiAuthConfirmation] = useState(false);
  const isEnterprise                                              = formState?.[GroupFormFields.Enterprise]?.value;
  const isAutojobs                                                = formState?.[GroupFormFields.AutojobsEnabled]?.value;
  const isVehicleModule                                           = formState?.[GroupFormFields.VehicleModule]?.value;
  const isCalampSupportRole                                       = useVbusAccess(true);
  
  const multiAuthRaw       = (formState?.features?.value as GenericObject)?.[AccountFeature.MultiAuth];
  const isMultiAuthPending = [
    MultiAuthStatus.PendingEnable,
    MultiAuthStatus.PendingDisable,
    MultiAuthStatus.PendingAccountEnabled
  ].includes(multiAuthRaw);
  const multiAuthTooltip   = translate(`admin-groups:dialog.tooltips.${hasSSOConfig
    ? 'multiAuthSsoToolTip'
    : 'multiAuthToolTip'}`);
  const isMfaFeatureEnabled = get('mfaEnabled');
  const hasInitializedMultiAuthScope = useRef(false);

  const showAutojobsToggle = !isCalAmpRootAccount && isCalampSupportRole && hasAutoJobs && autojobsSetting;

  useEffect(() => {
    if (isEditMode) {
      getEnterpriseApplications(group?.subscription);
      if (showServiceProfile && isCalampSupportRole) {
        getServiceProfile(entityId(group));
      }
    }
  }, [open, group, account, showServiceProfile, isCalampSupportRole]);

  useEffect(() => {
    hasInitializedMultiAuthScope.current = [
      MultiAuthStatus.AccountEnabled,
      MultiAuthStatus.Enabled,
      MultiAuthStatus.PendingAccountEnabled,
      MultiAuthStatus.PendingEnable
    ].includes(multiAuthRaw);
  }, [open, group, account, multiAuthRaw]);

  useEffect(() => {
    hasInitializedMultiAuthScope.current = [
      MultiAuthStatus.AccountEnabled,
      MultiAuthStatus.Enabled,
      MultiAuthStatus.PendingAccountEnabled,
      MultiAuthStatus.PendingEnable
    ].includes(multiAuthRaw);
  }, [open, group, account, multiAuthRaw]);

  const getEnterpriseApplications = useCallback((val) => {
    (async function() {
      if (isEnterprise) {
        const subscriptionData = await getSubscription(entityId(val?.href));
        const defaultApp       = subscriptionData?.applications?.map(app => app.title);
        setEnterpriseApps(defaultApp);
      }
    })();
  }, [isEnterprise]);

  const getServiceProfile = useCallback((accountId: number) => {
    (async function() {
      const serviceProfile = await getAccountServiceProfile(accountId);
      if (isEqual(entityId(serviceProfile?.account), accountId)) {
        setDefaultProfile(serviceProfile as ServiceProfile);
        handleChooserChange({
          name  : GroupFormFields.ServiceProfile,
          value : serviceProfile
        });
      } else {
        setDefaultProfile({name: 'None'} as ServiceProfile);
      }
    })();
  }, []);

  const onServiceProfileChange = useCallback((value) => {
    const accountSp: ServiceProfile= value
      ? defaultProfile
        ? {...value, id: defaultProfile.id, spVersion: value?.version, version: defaultProfile?.version}
        : {...value, spVersion: value.version}
      : {name: 'None', id: defaultProfile?.id};
    setDefaultProfile(accountSp);
    handleChooserChange({
      name  : GroupFormFields.ServiceProfile,
      value : accountSp
    });
  }, [defaultProfile]);

  const onToggleAutoJobsEnabled = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    if (!checked && isAutojobs) {
      setShowConfirmation(true);
    } else {
      handleSwitchChange(event, checked);
    }
  }, [handleSwitchChange, isAutojobs]);

  const handleConfirmationResult = useCallback((confirm: boolean) => {
    setShowConfirmation(false);
    if (confirm) {
      const autojobClickEvent = {
        target : {name: GroupFormFields.AutojobsEnabled, value: false, checked: false}
      } as unknown as ChangeEvent<HTMLInputElement>;
      handleSwitchChange(autojobClickEvent, false);
    }
  }, [handleSwitchChange]);

  const onToggleMultiAuth = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const checked    = event.target.checked;
    const isEnabling = checked && !formState?.[GroupFormFields.MultiAuth]?.value;
    if (isEnabling) {
      setShowMultiAuthConfirmation(true);
    } else {
      handleSwitchChange(event, checked);
      handleSwitchChange(buildFieldEvent(GroupFormFields.MultiAuthConfigured, true), true);
    }
  }, [handleSwitchChange, formState]);

  const onMultiAuthConfirmationResult = useCallback((confirm: boolean) => {
    setShowMultiAuthConfirmation(false);
    if (confirm) {
      handleSwitchChange(buildFieldEvent(GroupFormFields.MultiAuth, true), true);
      handleSwitchChange(buildFieldEvent(GroupFormFields.MultiAuthConfigured, true), true);
      if (!hasInitializedMultiAuthScope.current) {
        handleSwitchChange(buildFieldEvent(GroupFormFields.MultiAuthApplyToAllSubaccounts, true), true);
        hasInitializedMultiAuthScope.current = true;
      }
    }
  }, [handleSwitchChange]);

  const onToggleMultiAuthApplyToAllSubaccounts = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    handleSwitchChange(event, event.target.checked);
    handleSwitchChange(buildFieldEvent(GroupFormFields.MultiAuthConfigured, true), true);
    hasInitializedMultiAuthScope.current = true;
  }, [handleSwitchChange]);

  return (
    <FormSection bottomBorder
      title={translate('admin-groups:dialog.general')}
      legend={translate('common:editors.shared.fieldsRequired')}>
      <>
        <FormItem fullWidth={!isEditMode}>
          <FormTextField required
            disabled={isViewMode || isLoggedinUserAccount}
            fieldLabel={translate('common:editors.group.label')}
            placeholder={translate('common:editors.group.placeholder')}
            name={GroupFormFields.GroupName}
            onChange={handleFieldChange}
            hasError={formState[GroupFormFields.GroupName]?.error}
            inputProps={{maxLength: 64}}
            errorText={translate('common:editors.shared.required')}
            defaultValue={formState[GroupFormFields.GroupName]?.value} />
        </FormItem>
        {isEditMode && (
          <FormItem>
            <FormTextField required
              disabled={isEditMode}
              fieldLabel={translate('admin-groups:grid.columns.accountId')}
              placeholder={translate('admin-groups:grid.columns.accountId')}
              name={GroupFormFields.AccountId}
              defaultValue={group?.id} />
          </FormItem>
        )}
        <FormItem>
          <EntityStatusChooser entity={Entity.account}
            readOnly={isViewMode || isLoggedinUserAccount}
            onChange={function(value) {
              handleChooserChange({
                name : GroupFormFields.Status,
                value
              });
            }}
            status={formState[GroupFormFields.Status]?.value || EntityStatus.Enabled} />
        </FormItem>
        <FormItem>
          <GroupChooser required
            showAccountId={isEditMode}
            readOnly={isViewMode || !isEditMode || isLoggedinUserAccount}
            useDisabledStyles={isViewMode || !isEditMode}
            account={defaultAccount}
            includeGroupProperties={false}
            parentAccount={parentAccount}
            fieldLabel={translate('common:editors.parentGroup.label')}
            onChange={function(value) {
              handleChooserChange({
                name     : GroupFormFields.ParentGroup,
                value,
                required : true
              });
            }}
            hasError={formState[GroupFormFields.ParentGroup]?.error}
            defaultValue={formState[GroupFormFields.ParentGroup]?.value || account}
            primaryButtonDisabled={isSameGroup} />
        </FormItem>
        <FormItem>
          <div className={classes.flexStyle}>
            <div className={classes.leftDiv}>
              <FormTextField disabled={isViewMode || isLoggedinUserAccount}
                fieldLabel={translate('common:editors.solomonId.label')}
                placeholder={translate('common:editors.solomonId.placeholder')}
                name={GroupFormFields.SolomonId}
                onChange={handleSolomonFieldChange}
                hasError={formState[GroupFormFields.SolomonId]?.error}
                value={(formState[GroupFormFields.SolomonId]?.value)?.trim()}
                maxLength={32} />
            </div>
            <div className={classes.tooltipDiv}>
              <AccountTooltip title={translate('admin-groups:dialog.tooltips.solomonIdToolTip')} />
            </div>
          </div>
        </FormItem>
        <FormItem>
          <div className={classes.flexStyle}>
            <div className={classes.leftDiv}>
              <FormTextField disabled={isViewMode || isLoggedinUserAccount}
                fieldLabel={translate('common:editors.referenceERPID.label')}
                placeholder={translate('common:editors.referenceERPID.placeholder')}
                name={GroupFormFields.ExternalRefID}
                onChange={handleExternalRefIdFieldChange}
                hasError={formState[GroupFormFields.ExternalRefID]?.error}
                defaultValue={(formState[GroupFormFields.ExternalRefID]?.value)?.trim()}
                value={(formState[GroupFormFields.ExternalRefID]?.value)?.trim()}
                maxLength={64} />
            </div>
            <div className={classes.tooltipDiv}>
              <AccountTooltip title={translate('admin-groups:dialog.tooltips.refrenceERPToolTip')} />
            </div>
          </div>
        </FormItem>
        <FormItem>
          <div className={classes.switchContainer}>
            <AccountType classNames={classes}
              handleSwitchChange={handleSwitchChange}
              isLoggedinUserAccount={isLoggedinUserAccount}
              isCalampSupport={isCalampSupport}
              isEnterprise={isEnterprise}
              isViewMode={isViewMode}
              isEditMode={isEditMode}
              isPhone={isPhone} />
            <Render.If condition={isMfaFeatureEnabled}>
              <div className={classes.flexStyle} style={{paddingLeft: '5px'}}>
                <div>
                  <Switch label={translate('admin-groups:dialog.multiAuth.label')}
                    marginRight={isPhone ? '25px' : '10px'}
                    name={GroupFormFields.MultiAuth}
                    disabled={isViewMode || isMultiAuthPending || hasSSOConfig}
                    onChange={onToggleMultiAuth}
                    checked={formState?.[GroupFormFields.MultiAuth]?.value} />
                  <Render.If condition={isMultiAuthPending}>
                    <BodyTypography>
                      {translate(multiAuthPendingMessageKey[multiAuthRaw])}
                    </BodyTypography>
                  </Render.If>
                  <Render.If condition={formState?.[GroupFormFields.MultiAuth]?.value}>
                    <div className={classes.multiAuthSubOption}>
                      <CheckBox label={translate('admin-groups:dialog.multiAuth.applyToAllSubaccounts')}
                        name={GroupFormFields.MultiAuthApplyToAllSubaccounts}
                        disabled={isViewMode || isMultiAuthPending || hasSSOConfig}
                        onChange={onToggleMultiAuthApplyToAllSubaccounts}
                        checked={formState?.[GroupFormFields.MultiAuthApplyToAllSubaccounts]?.value} />
                    </div>
                  </Render.If>
                </div>
                <div className={classes.textTooltip}>
                  <AccountTooltip title={multiAuthTooltip} />
                </div>
              </div>
            </Render.If>
            <ConditionalRender condition={showAutojobsToggle}>
              <div className={classes.switch}>
                <div className={classes.overridesSwitches}>
                  <Switch label={translate('common:editors.autojobs.label')}
                    marginRight={isPhone ? '25px' : '0px'}
                    name={GroupFormFields.AutojobsEnabled}
                    onChange={onToggleAutoJobsEnabled}
                    disabled={isViewMode || !hasAutoJobs}
                    checked={isAutojobs} />
                </div>
                <AutojobsTooltip enabled={hasAutoJobs && showAutojobsToggle} />
              </div>
            </ConditionalRender>
          </div>
        </FormItem>
        <FormItem>
          <div className={classes.flexStyle}>
            <div className={classes.leftDiv}>
              <SubscriptionChooser accountId={entityId(account)}
                isEditMode={isEditMode}
                isCalampSupport={isCalampSupport}
                required={isEnterprise}
                includeNone={false}
                isViewMode={isViewMode}
                readOnly={isViewMode || !isEnterprise || isLoggedinUserAccount}
                onChange={function(value) {
                  getEnterpriseApplications(value);
                  handleChooserChange({
                    name : GroupFormFields.Subscription,
                    value
                  });
                }}
                validationError={formState[GroupFormFields.Subscription]?.error}
                defaultValue={formState[GroupFormFields.Subscription]?.value || ''} />
            </div>
            <div className={classes.tooltipDiv}>
              <AccountTooltip title={translate('admin-groups:dialog.tooltips.subscriptionToolTip')} />
            </div>
          </div>
        </FormItem>
        {isEnterprise && formState[GroupFormFields.Subscription]?.value?.id && (
          <FormItem>
            <BodyTypography className={classes.fieldLabel}>
              {translate('admin-groups:dialog.fields.enterpriseApplications')}
            </BodyTypography>
            <div className={classes.appStyle}>
              {enterpriseApps?.map((app, index) => {
                return (
                  <div key={`${app}_${index}`}>
                    <Chip label={app}
                      size='small'
                      maxWidth={false}
                      borderRadius='56px'
                      labelColor={dlsColor('grey2')}
                      hoverDefault
                      showCustomDeleteIcon
                      noTextTransform />
                  </div>
                );
              })}
            </div>
          </FormItem>
        )}
        {showServiceProfile && isCalampSupportRole && (<FormItem>
          <div className={classes.flexStyle}>
            <div className={classes.leftDiv}>
              <ServiceProfileChooser includeNone
                readOnly={isViewMode || isLoggedinUserAccount}
                onChange={onServiceProfileChange}
                validationError={formState[GroupFormFields.ServiceProfile]?.error}
                defaultValue={defaultProfile} />
            </div>
          </div>
        </FormItem>)}
        <FormItem>
          <div className={classes.vehicleModule}>
            {translate('admin-groups:dialog.fields.vehicleModule')}
          </div>
          <div className={classes.switchContainer}>
            <div className={classes.switch}>
              <Switch label={translate('common:editors.vehicleModule.label')}
                marginRight={isPhone ? '25px' : '0px'}
                name={GroupFormFields.VehicleModule}
                onChange={handleSwitchChange}
                disabled={isViewMode}
                checked={isVehicleModule} />
              <AccountTooltip title={
                <div>
                  <p>{translate('admin-groups:dialog.tooltips.vehicleModuleTooltip')}</p>
                  <p>{translate('admin-groups:dialog.tooltips.vehicleModuleTooltipAdditional')}</p>
                </div>
              } align />
            </div>
          </div>
        </FormItem>
      </>
      <ConditionalRender condition={showConfirmation}>
        <AutojobsSettingConfirmationDialog onAction={handleConfirmationResult} enabled />
      </ConditionalRender>
      <ConditionalRender condition={showMultiAuthConfirmation}>
        <MultiAuthConfirmationDialog onAction={onMultiAuthConfirmationResult} />
      </ConditionalRender>
    </FormSection>
  );
};

export default AccountInfo;
